import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Mail, Loader2, CheckCircle2, AlertCircle, Shield, ArrowRight, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TaxSetu Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

/**
 * GSTR-1 Access Gate Component - Email Only Step
 * Flow: Enter Email → Credentials sent → Show GST Portal Login
 */
const GSTR1AccessGate = ({ onAccessGranted }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasAutoRequested = useRef(false); // Prevent double auto-requests

  // Handle back to dashboard — go to the GST hub (was incorrectly hard-coded to /pw)
  const handleBackToDashboard = () => {
    navigate('/gst');
  };

  // Check for pre-filled email from PW batch (only once)
  useEffect(() => {
    // Prevent multiple auto-requests
    if (hasAutoRequested.current) return;
    
    const pwEmail = localStorage.getItem('pw_batch_email');
    const leadEmail = localStorage.getItem('practiq_lead_email');
    const preFilledEmail = pwEmail || leadEmail;
    
    // Check if already requested recently (within last 30 seconds)
    const lastRequestTime = localStorage.getItem('gstr1_last_request_time');
    const lastRequestEmail = localStorage.getItem('gstr1_last_request_email');
    const now = Date.now();
    
    if (lastRequestTime && lastRequestEmail === preFilledEmail) {
      const timeDiff = now - parseInt(lastRequestTime);
      if (timeDiff < 30000) { // 30 seconds cooldown
        // Skip auto-request, just pre-fill email and proceed
        if (preFilledEmail) {
          setEmail(preFilledEmail);
          hasAutoRequested.current = true;
          onAccessGranted({ email: preFilledEmail, step: 'login' });
        }
        return;
      }
    }
    
    if (preFilledEmail) {
      setEmail(preFilledEmail);
      hasAutoRequested.current = true;
      // Auto-request credentials
      handleRequestAccessInternal(preFilledEmail);
    }
  }, []);

  const handleRequestAccessInternal = async (emailToUse) => {
    if (!emailToUse.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    // Store request timestamp to prevent duplicates
    localStorage.setItem('gstr1_last_request_time', Date.now().toString());
    localStorage.setItem('gstr1_last_request_email', emailToUse.trim());
    
    try {
      const response = await fetch(`${API_URL}/api/gstr1-access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToUse.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store email for later use
        localStorage.setItem('gstr1_pending_email', emailToUse.trim());
        // Proceed to GST Portal Login
        onAccessGranted({ email: emailToUse.trim(), step: 'login' });
      } else {
        setError(data.detail || 'Failed to request access');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    if (e) e.preventDefault();
    await handleRequestAccessInternal(email);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b1e3f] via-[#1e3a5f] to-[#2a5298] flex flex-col p-4">
      {/* Back Button - Top Left */}
      <div className="absolute top-4 left-4">
        <button
          onClick={handleBackToDashboard}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg border border-white/20 hover:border-white/30 transition-all"
          data-testid="gstr1-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <img src={TAXSETU_LOGO} alt="TaxSetu" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">GSTR-1 Simulator</h1>
            <p className="text-gray-300 mt-2">Practice GST Filing in a Risk-Free Environment</p>
          </div>

        {/* Access Card */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          <form onSubmit={handleRequestAccess}>
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5298] p-6 text-center">
              <Shield className="w-12 h-12 text-white mx-auto mb-2" />
              <h2 className="text-xl font-semibold text-white">Get Started</h2>
              <p className="text-blue-200 text-sm mt-1">Enter your email to receive login credentials</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="pl-10 h-12"
                    data-testid="gstr1-email-input"
                    autoFocus
                    disabled={loading}
                  />
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-[#1e3a5f] hover:bg-[#2a5298] text-white font-semibold"
                disabled={loading}
                data-testid="gstr1-request-access-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending Credentials...</>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-gray-500">
                Username & Password will be sent to your email
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          © 2026 PractiQ | Conceptualized by CA Shivangi Mittal
        </p>
        </div>
      </div>
    </div>
  );
};

export default GSTR1AccessGate;
